import { useState } from "react";
import { toast } from "sonner";

import { ProjectResourcesPanel } from "@/components/project-resources-panel";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { ProjectSummary } from "@/lib/api";
import { getLinkedResourceNames } from "@/lib/project-status";

interface ProjectDeleteDialogProps {
  project: ProjectSummary | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** A resource was deleted or unlinked: the caller's links and badges are stale. */
  onResourcesChanged: () => void;
  /** The project record is gone; called just before the dialog closes. */
  onProjectDeleted?: () => void;
}

/**
 * Admin delete for a project (BOIE-338): tear down its connected resources,
 * then remove the record.
 *
 * It runs the same teardown section as the project-detail dialog's Resources
 * tab, so "Delete all" reports each resource's outcome the same way, and it
 * removes the record only once every resource is gone. Removing the record
 * first would orphan whatever is still running, and the backend refuses it.
 *
 * Opened from the admin Projects page and from Home's out-of-sync dialog.
 * Callers key it by project id, so another project remounts it.
 */
export function ProjectDeleteDialog({
  project,
  open,
  onOpenChange,
  onResourcesChanged,
  onProjectDeleted,
}: ProjectDeleteDialogProps) {
  // Mirrors the panel's per-resource deletions, so its rows and the copy above
  // them follow the teardown without waiting for the list to refetch. Seeded
  // once: callers key this dialog by project, so another project remounts it.
  const [localProject, setLocalProject] = useState<ProjectSummary | null>(project);
  // While a deletion runs, the dialog stays open: closing it would unmount the
  // panel, losing the per-resource failure report, and a reopened panel could
  // start a second teardown of the same project alongside the first.
  const [busy, setBusy] = useState(false);

  if (!project) {
    return null;
  }

  const p = localProject ?? project;
  const linked = getLinkedResourceNames(p);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Outside click, Escape and the close button all land here; the
        // close after a finished delete calls `onOpenChange` directly.
        if (!next && busy) {
          return;
        }
        onOpenChange(next);
      }}
    >
      <DialogContent
        className="sm:max-w-2xl"
        showCloseButton={!busy}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Delete {p.project_name}</DialogTitle>
          <DialogDescription>
            {linked.length > 0
              ? `Deleting this project permanently deletes ${linked.join(", ")}, then removes the project record. If a resource cannot be deleted, it is named below and the record is kept.`
              : "Nothing is connected to this project any more, so deleting it only removes the project record."}
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <ProjectResourcesPanel
            project={p}
            canManage
            variant="admin"
            onBusyChange={setBusy}
            onResourceDeleted={(updates) => {
              setLocalProject((prev) => (prev ? { ...prev, ...updates } : prev));
              onResourcesChanged();
            }}
            onProjectDeleted={() => {
              toast.success(`${p.project_name} was deleted.`);
              onProjectDeleted?.();
              onOpenChange(false);
            }}
          />
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
